import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart3, BadgeCheck, Building, FileSignature, LogOut, Mail, MessageSquare, Settings,
  Shield, ShieldAlert, User, Users, X, Zap,
} from "lucide-react";
import { ROLE_LABELS } from "../../lib/dashConstants";
import { API } from "../../lib/api";

const INITIALS = (s) => (s || "U").trim().slice(0, 2).toUpperCase();

export default function Sidebar({ open, onClose, user, perms, activeView, onNavigate, onLogout }) {
  const [chatUnread, setChatUnread] = useState(0);
  // Poll unread chat count every 8s
  useEffect(() => {
    let alive = true;
    const tick = () => {
      axios.get(`${API}/chat/unread-count`)
        .then((r) => { if (alive) setChatUnread(r.data?.unread || 0); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Lock body scroll while sidebar is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const nav = (view) => () => { onNavigate(view); onClose(); };

  return (
    <>
      {open && <div className="sidebar-backdrop" onClick={onClose} data-testid="sidebar-backdrop" />}
      <aside className={`sidebar ${open ? "open" : ""}`} data-testid="sidebar">
        <div className="sidebar-head">
          <h2>IEU Sales-RM Activity</h2>
          <div className="sub">Dashboard · Enterprise CRM</div>
          <div className="sidebar-user">
            <div className="avatar">{INITIALS(user?.username)}</div>
            <div className="meta">
              <b>{user?.name || user?.username}</b>
              <span>{ROLE_LABELS[user?.role] || user?.role}</span>
            </div>
            <button className="icon-btn" onClick={onClose} style={{color: "#fff"}} data-testid="sidebar-close">
              <X size={16} />
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-title">Workspace</div>
          <button className={`sidebar-item ${activeView === "dashboard" ? "active" : ""}`}
                  onClick={nav("dashboard")} data-testid="sidebar-dashboard">
            <BarChart3 size={16} /><span className="lbl">Dashboard</span>
          </button>
          {/* Leads: admin/manager/sales_tl/sales (not RM) */}
          {["admin", "manager", "sales_tl", "sales"].includes(user.role) && (
            <button className={`sidebar-item ${activeView === "leads" ? "active" : ""}`}
                    onClick={nav("leads")} data-testid="sidebar-leads">
              <Users size={16} /><span className="lbl">Leads</span>
            </button>
          )}
          {/* Active Account: visible to all roles per spec */}
          <button className={`sidebar-item ${activeView === "active-accounts" ? "active" : ""}`}
                  onClick={nav("active-accounts")} data-testid="sidebar-active-accounts">
            <BadgeCheck size={16} /><span className="lbl">Active Account</span>
          </button>
          {/* Churn Account: visible to all */}
          <button className={`sidebar-item ${activeView === "churn-accounts" ? "active" : ""}`}
                  onClick={nav("churn-accounts")} data-testid="sidebar-churn-accounts">
            <ShieldAlert size={16} /><span className="lbl">Churn Account</span>
          </button>
          {/* Forecast: admin/manager only */}
          {["admin", "manager"].includes(user.role) && (
            <button className={`sidebar-item ${activeView === "forecast" ? "active" : ""}`}
                    onClick={nav("forecast")} data-testid="sidebar-forecast">
              <BarChart3 size={16} /><span className="lbl">Forecast</span>
            </button>
          )}
          {/* Sales Comparison: admin/manager only */}
          {["admin", "manager"].includes(user.role) && (
            <button className={`sidebar-item ${activeView === "comparison" ? "active" : ""}`}
                    onClick={nav("comparison")} data-testid="sidebar-comparison">
              <BarChart3 size={16} /><span className="lbl">Sales Comparison</span>
            </button>
          )}
          {/* Approval: admin + sales_tl (sales_tl approves promotions) */}
          {["admin", "sales_tl", "manager"].includes(user.role) && (
            <button className={`sidebar-item ${activeView === "approval" ? "active" : ""}`}
                    onClick={nav("approval")} data-testid="sidebar-approval">
              <Building size={16} /><span className="lbl">Approval</span>
            </button>
          )}
          <button className={`sidebar-item ${activeView === "chat" ? "active" : ""}`}
                  onClick={nav("chat")} data-testid="sidebar-chat">
            <MessageSquare size={16} /><span className="lbl">Chat / Messenger</span>
            {chatUnread > 0 && (
              <span data-testid="chat-unread-badge" style={{
                marginLeft: "auto", background: "var(--danger)", color: "#fff",
                borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700,
                minWidth: 22, textAlign: "center",
              }}>{chatUnread > 99 ? "99+" : chatUnread}</span>
            )}
          </button>
          <button className={`sidebar-item ${activeView === "eft" ? "active" : ""}`}
                  onClick={nav("eft")} data-testid="sidebar-eft">
            <Zap size={16} /><span className="lbl">EFT Activity</span>
          </button>

          {perms.user_mgmt && (
            <>
              <div className="sidebar-section-title">Administration</div>
              <button className={`sidebar-item ${activeView === "users" ? "active" : ""}`}
                      onClick={nav("users")} data-testid="sidebar-users">
                <Users size={16} /><span className="lbl">User Management</span>
              </button>
              <button className={`sidebar-item ${activeView === "audit" ? "active" : ""}`}
                      onClick={nav("audit")} data-testid="sidebar-audit">
                <Shield size={16} /><span className="lbl">Audit Log</span>
              </button>
            </>
          )}

          {(perms.audit) && (
            <button className="sidebar-item" onClick={nav("audit-email")} data-testid="sidebar-audit-email">
              <Mail size={16} /><span className="lbl">Kirim Audit Email</span>
            </button>
          )}

          <div className="sidebar-section-title">Akun Saya</div>
          <button className="sidebar-item" onClick={nav("profile")} data-testid="sidebar-profile">
            <User size={16} /><span className="lbl">Profile</span>
          </button>
          {perms.change_creds && (
            <button className="sidebar-item" onClick={nav("settings")} data-testid="sidebar-settings">
              <Settings size={16} /><span className="lbl">Ganti Kredensial</span>
            </button>
          )}
        </nav>

        <div className="sidebar-foot">
          <button className="sidebar-logout" onClick={onLogout} data-testid="sidebar-logout">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>
    </>
  );
}
